import { MapPin, Clock, Users, Star, ShoppingCart, Plus, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TripDisplay, generateSlug } from "@/lib/trips";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useState } from "react";

interface TripCardProps {
  trip: TripDisplay;
  index: number;
}

const TripCard = ({ trip, index }: TripCardProps) => {
  const { addToCart, getItemQuantity, updateQuantity } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [imageError, setImageError] = useState(false);

  const cartQuantity = getItemQuantity(trip.id);
  const isInCart = cartQuantity > 0;
  const isFullyBooked = trip.availability_status === "fully_booked";

  // Parse group_size to get max quantity
  const getMaxQuantity = (groupSize: string): number => {
    const match = groupSize.match(/(\d+)/g);
    if (match && match.length > 0) {
      const max = parseInt(match[match.length - 1], 10);
      return max || 20;
    }
    return 20;
  };

  const maxQty = getMaxQuantity(trip.groupSize);

  const handleCardClick = () => {
    const slug = generateSlug(trip.title);
    navigate(`/trip/${slug}`, { state: { trip } });
  };

  const handleIncreaseQty = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (quantity < maxQty) {
      setQuantity(quantity + 1);
    } else {
      toast.info(`Maximum ${maxQty} travelers allowed for this trip`);
    }
  };

  const handleDecreaseQty = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!user) {
      toast.info("Please sign in to add items to your cart");
      navigate("/auth", { state: { returnTo: window.location.pathname, action: "addToCart", tripId: trip.id } });
      return;
    }

    if (isInCart) {
      updateQuantity(trip.id, cartQuantity + quantity);
      toast.success(`Added ${quantity} more to cart!`, {
        description: `${trip.title} - Total: ${cartQuantity + quantity}`,
      });
    } else {
      addToCart(trip, quantity);
    toast.success(`${trip.title} added to cart!`, {
        description: `Quantity: ${quantity} × ${trip.duration} adventure in ${trip.destination}`,
    });
    }
    setQuantity(1);
  };

  return (
    <Card 
      className="group overflow-hidden border-0 shadow-card hover:shadow-elevated transition-all duration-500 animate-fade-in bg-card cursor-pointer"
      style={{ animationDelay: `${index * 0.1}s` }}
      onClick={handleCardClick}
    >
      <div className="relative h-56 overflow-hidden bg-muted">
        {imageError ? (
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <div className="text-center p-4">
              <div className="text-4xl mb-2">🖼️</div>
              <p className="text-xs text-muted-foreground">Image not available</p>
            </div>
          </div>
        ) : (
          <img
            src={trip.image}
            alt={trip.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            onError={() => setImageError(true)}
            onLoad={() => setImageError(false)}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent" />
        
        <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground">
          {trip.category}
        </Badge>

        {/* Availability Status Badge */}
        {trip.availability_status && (
          <Badge
            className={`absolute top-4 right-4 text-sm px-3 py-1 ${
              trip.availability_status === "available"
                ? "bg-green-500/90 text-white border-green-600"
                : trip.availability_status === "few_slots"
                ? "bg-orange-500/90 text-white border-orange-600"
                : "bg-red-500/90 text-white border-red-600"
            }`}
          >
            {trip.availability_status === "available"
              ? "Available"
              : trip.availability_status === "few_slots"
              ? "Few Slots Left"
              : "Fully Booked"}
          </Badge>
        )}
        
        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center gap-1 text-primary-foreground mb-1">
            <Star className="w-4 h-4 fill-accent text-accent" />
            <span className="text-sm font-medium">{trip.rating}</span>
          </div>
          <h3 className="font-display text-xl font-semibold text-primary-foreground">
            {trip.title}
          </h3>
        </div>
      </div>

      <CardContent className="p-5">
        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
          <span className="flex items-center gap-1">
            <MapPin className="w-4 h-4" />
            {trip.destination}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {trip.duration}
          </span>
        </div>

        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {trip.description}
        </p>

        <div className="flex items-center gap-1 text-sm text-muted-foreground mb-4">
          <Users className="w-4 h-4" />
          <span>10 guys + 10 girls</span>
        </div>

        <div className="flex flex-wrap gap-2 mb-5">
          {trip.highlights.slice(0, 3).map((highlight) => (
            <span
              key={highlight}
              className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground"
            >
              {highlight}
            </span>
          ))}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div>
            <span className="text-2xl font-bold text-foreground">₹{trip.price.toLocaleString('en-IN')}</span>
            <span className="text-sm text-muted-foreground">/person</span>
          </div>
          <div className="flex items-center gap-2">
            {isInCart && (
              <span className="text-xs text-muted-foreground">In cart: {cartQuantity}</span>
            )}
            <div className="flex items-center gap-1 border border-border rounded-md" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-muted"
                onClick={handleDecreaseQty}
                disabled={quantity <= 1 || isFullyBooked}
              >
                <Minus className="w-3 h-3" />
              </Button>
              <span className="w-8 text-center text-sm font-medium text-foreground">
                {quantity}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-muted"
                onClick={handleIncreaseQty}
                disabled={quantity >= maxQty || isFullyBooked}
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
            <Button 
              onClick={handleAddToCart} 
              variant="cart" 
              className="gap-2" 
              size="sm"
              disabled={isFullyBooked}
            >
              <ShoppingCart className="w-4 h-4" />
              {isFullyBooked ? "Fully Booked" : isInCart ? "Add More" : "Add to Cart"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TripCard;
